import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ApplicationAdvisorViewNavigationState } from '../../models/application-advisor-view-navigation-state';
import { inject } from '@angular/core';
import { ApplicationService } from '../../services/application/application.service';
import { UserStateService } from '../../../../user/shared/services/user-state/user-state.service';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { UserRole } from '../../../../../core/enums/users/role';

export const ApplicationAdvisorViewResolver = (
    route: ActivatedRouteSnapshot,
): Observable<void> => {
    const router = inject(Router);
    const applicationService = inject(ApplicationService);
    const userState = inject(UserStateService);

    if (userState.getRole() !== UserRole.Advisor) {
        // Only attempt to mark as viewed if the user is an advisor
        return of(undefined);
    }

    const navigation = router.getCurrentNavigation();
    const state = navigation?.extras?.state as
        | ApplicationAdvisorViewNavigationState
        | undefined;

    if (state?.justCreated) {
        // If the user is navigating from just having created the application,
        // do not mark the application as viewed by the advisor.
        return of(undefined);
    }

    // For FRN Filing, the applicationId can be retrieved from firstChild
    const applicationId =
        route.paramMap.get('id') ?? route.firstChild?.paramMap.get('id');

    if (!applicationId) {
        // applicationId may be null, e.g. create FRN Filing route
        return of(undefined);
    }

    return applicationService
        .getApplicationAdvisorViewStatus(applicationId)
        .pipe(
            switchMap((response) => {
                const shouldMarkAsViewed =
                    !response.isViewedByAdvisor &&
                    userState.getUser()?.id == response.advisorId;

                if (!shouldMarkAsViewed) {
                    return of(undefined);
                }

                return applicationService
                    .markApplicationAsViewedByAdvisor(applicationId)
                    .pipe(
                        map(() => undefined),
                        catchError((err) => {
                            console.error(
                                'Failed to mark application as viewed by advisor',
                                err,
                            );
                            return of(undefined);
                        }),
                    );
            }),
            catchError((err) => {
                console.error(
                    'Failed to get application advisor view status',
                    err,
                );
                return of(undefined);
            }),
        );
};
